import { useEffect, useState, useRef } from "react";
import { useStore } from "../store/globalState";
import "./ScrollToTop.css";

interface ScrollToTopProps {
  scrollThreshold?: number;
  scrollableSelector?: string;
}

export const ScrollToTop = ({
  scrollThreshold = 200,
  scrollableSelector,
}: ScrollToTopProps) => {
  // Partner mesh (gradient) paints the pill; the button text slot paints the arrow.
  const { backgroundBtn, textColorBtn } = useStore().theme.whiteView;
  const [isVisible, setIsVisible] = useState(false);
  const scrollableElementRef = useRef<Element | Window | null>(null);

  useEffect(() => {
    let scrollableElement: Element | Window | null = null;

    if (scrollableSelector) {
      scrollableElement = document.querySelector(scrollableSelector);
    } else {
      const commonSelectors = [
        ".legal-content",
        "[class*='content']",
        "[class*='overflow']",
      ];

      for (const selector of commonSelectors) {
        const element = document.querySelector(selector);
        if (
          element &&
          element instanceof Element &&
          element.scrollHeight > element.clientHeight
        ) {
          scrollableElement = element;
          break;
        }
      }

      if (!scrollableElement) scrollableElement = window;
    }

    scrollableElementRef.current = scrollableElement;

    const handleScroll = () => {
      if (scrollableElement === window)
        setIsVisible(window.scrollY > scrollThreshold);

      if (scrollableElement && scrollableElement instanceof Element)
        setIsVisible(scrollableElement.scrollTop > scrollThreshold);
    };

    const target = scrollableElement === window ? window : scrollableElement;
    target?.addEventListener("scroll", handleScroll);

    handleScroll();

    return () => {
      target?.removeEventListener("scroll", handleScroll);
    };
  }, [scrollThreshold, scrollableSelector]);

  const scrollToTop = () => {
    const element = scrollableElementRef.current;

    if (!element) return;

    if (element === window) {
      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    } else if (element instanceof Element) {
      element.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    }
  };

  if (!isVisible) return null;

  return (
    <button
      className="scroll-to-top"
      style={{ background: backgroundBtn, color: textColorBtn }}
      onClick={scrollToTop}
      aria-label="Scroll to top"
      title="Scroll to top"
    >
      <span className="scroll-to-top-icon">↑</span>
    </button>
  );
};
